import GolfIcon from '@/components/GolfIcon';
import { Badge } from '@/components/ui/badge';
import { GolfAvatar } from '@/components/ui/GolfAvatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useUserProfile } from '@/context/UserProfileContext';
import { lightImpact, signOut } from '@/utils/capacitorUtils';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { IonButton, IonContent, IonHeader, IonItem, IonLabel, IonList, IonPage, IonToolbar } from '@ionic/react';
import { chevronExpandOutline } from 'ionicons/icons';
import { BugIcon, DraftingCompass, Flag, LogOut, Luggage, Settings, Sparkles, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

// Debug Toggle Component
function DebugToggle() {
  const [clickCount, setClickCount] = useState(0);
  const [isDebugMode, setIsDebugMode] = useState(false);

  // Check initial debug state
  useEffect(() => {
    const checkDebugState = async () => {
      const { value } = await Preferences.get({ key: 'isDebug' });
      setIsDebugMode(value === 'true');
    };

    checkDebugState();
  }, []);

  const handleVersionClick = async () => {
    // Increment click counter
    const newCount = clickCount + 1;
    setClickCount(newCount);

    // If clicked 9 times, toggle debug mode
    if (newCount >= 9) {
      // Reset counter
      setClickCount(0);

      // Toggle debug mode
      const newDebugMode = !isDebugMode;
      setIsDebugMode(newDebugMode);

      // Save to preferences
      await Preferences.set({
        key: 'isDebug',
        value: newDebugMode.toString()
      });

      console.log(`Debug mode ${newDebugMode ? 'enabled' : 'disabled'}`);
    }
  };

  return (
    <p onClick={handleVersionClick} className="cursor-pointer">
      AppVersion: Beta 1.1 {isDebugMode && '(Debug)'}
    </p>
  );
}

export function MenuPage() {
  const { profile } = useUserProfile();
  const { t } = useTranslation();
  const [isMetric, setIsMetric] = useState<boolean | null>(null);
  const [isDebugMode, setIsDebugMode] = useState(false);
  const history = useHistory();

  const toggleMeasurement = () => {
    setIsMetric(prev => !prev);
    lightImpact();
    Preferences.set({ key: 'isMetric', value: isMetric!.toString() });
  };

  useEffect(() => {
    const getIsMetric = async () => {
      const { value } = await Preferences.get({ key: 'isMetric' });
      setIsMetric(value === 'true');
    };

    const getIsDebug = async () => {
      const { value } = await Preferences.get({ key: 'isDebug' });
      setIsDebugMode(value === 'true');
    };

    getIsMetric();
    getIsDebug();
  }, []);

  return (
    <IonPage>
      <IonHeader className='ion-no-border bg-card'>
        <IonToolbar className="flex items-center justify-between px-2 py-2">
          <div className="flex items-center gap-4">
            <GolfAvatar imageUrl={profile?.imageUrl} name={profile?.name} size={64} />
            <div className="flex flex-col">
              <h2 className="text-xl font-semibold">{`${profile?.firstName} ${profile?.lastName}`}</h2>
              <div className="flex items-center justify-between gap-2">
                <Badge className="pointer-events-none mt-1 bg-orange-500 text-white">Early bird</Badge>
                <Badge className="pointer-events-none mt-1 bg-yellow-500 text-white">VIP</Badge>
                <Badge className="pointer-events-none mt-1 bg-[#003C80] text-white">Pro</Badge>
              </div>
            </div>
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent className='dark:bg-background'>
        <ScrollArea className="bg-secondary h-full dark:bg-background overflow-y-auto [-ms-scrollbar-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {/* Main Menu */}
          <div className="mb-8 flex-1 space-y-4 p-2">
            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-4">
              <IonButton color="transparent" routerLink={`/u/${profile?.username}`} fill='solid' type="button" className="bg-card rounded-md font-normal text-foreground w-full">
                {t('see_profile')}
              </IonButton>
              <IonButton color="transparent" routerLink={`/settings/edit-profile`} fill='solid' type="button" className="bg-card rounded-md font-normal text-foreground w-full">
                {t('edit_profile')}
              </IonButton>
            </div>
            {/* Sub Menu */}
            <IonList className="rounded-lg bg-card">
              <IonItem routerLink="/friends" detail={true}>
                <div slot="start" className="flex items-center justify-center size-8 rounded-md bg-orange-500/20">
                  <Users className="size-5 text-orange-500" />
                </div>
                <IonLabel>{t('friends')}</IonLabel>
              </IonItem>
              <IonItem routerLink="/my-bag" detail={true}>
                <div slot="start" className="flex items-center justify-center size-8 rounded-md bg-red-500/20">
                  <GolfIcon name="golf-bag-red" className="size-5 dark:invert-0" />
                </div>
                <IonLabel>{t('mybag')}</IonLabel>
              </IonItem>

              <IonItem onClick={toggleMeasurement} detail={true} detailIcon={chevronExpandOutline}>
                <div slot="start" className="flex items-center justify-center size-8 rounded-md bg-blue-500/20">
                  <DraftingCompass className="size-5 text-blue-500" />
                </div>
                <IonLabel>{t('unit')}</IonLabel>
                <div slot="end" className="flex items-center gap-2">
                  <span className="text-primary">{isMetric ? t('meters') : 'Yards'}</span>
                </div>
              </IonItem>

              <IonItem routerLink="/settings" routerDirection="forward" detail={true}>
                <div slot="start" className="flex items-center justify-center size-8 rounded-md bg-gray-500/20">
                  <Settings className="size-5 text-gray-500" />
                </div>
                <IonLabel>{t('settings')}</IonLabel>
              </IonItem>

              <IonItem href="https://mgp.supahub.com/" lines="none" target="_blank" detail={true}>
                <div slot="start" className="flex items-center justify-center size-8 rounded-md bg-yellow-500/20">
                  <Sparkles className="size-5 text-yellow-500" />
                </div>
                <IonLabel>Suggestions et Feedback</IonLabel>
              </IonItem>

              {isDebugMode && (
                <IonItem routerLink="/debug" detail={true} lines="none" className="ion-item-hover">
                  <div slot="start" className="flex items-center justify-center size-8 rounded-md bg-purple-500/20">
                    <BugIcon className="size-5 text-purple-500" />
                  </div>
                  <IonLabel>Debug Page</IonLabel>
                </IonItem>
              )}
            </IonList>
            <IonItem onClick={() => { signOut().then(() => history.push('/mobile')) }} lines="none" className="bg-card text-red-500 rounded-md">
              <div slot="start" className="flex items-center justify-center size-8 rounded-md bg-red-500/20">
                <LogOut className="size-5 text-red-500" />
              </div>
              <IonLabel>{t('signout')}</IonLabel>
            </IonItem>

            {/* App Version and User ID */}
            <div className="p-2 text-sm text-gray-500">
              <DebugToggle />
              <p>{`Profile ID: ${profile?.id}`}</p>
              <p>{`Plateform: ${Capacitor.getPlatform()}`}</p>
            </div>
          </div>
        </ScrollArea>
      </IonContent>
    </IonPage>
  );
}

export default MenuPage;
